/**
 * Brother PT-D610BT Label Printing Module
 * QR code focused labels with stock number
 * Supports all tape widths: 3.5mm, 6mm, 9mm, 12mm, 18mm, 24mm
 * Resolution: 360 DPI (Brother standard)
 */

import QRCode from 'qrcode';

const DPI = 360;
const MM_TO_PX = DPI / 25.4; // ~14.17 px per mm

// All supported tape widths and their label formats
export const LABEL_CATALOG = {
  '3.5mm': {
    tapeWidth: 3.5,
    printableHeight: Math.round(3.5 * MM_TO_PX),
    labels: [
      { id: '3.5x25', name: '3.5 × 25mm', width: 25, height: 3.5, px: { w: 354, h: 50 } },
      { id: '3.5x40', name: '3.5 × 40mm', width: 40, height: 3.5, px: { w: 567, h: 50 } },
    ]
  },
  '6mm': {
    tapeWidth: 6,
    printableHeight: Math.round(6 * MM_TO_PX),
    labels: [
      { id: '6x25', name: '6 × 25mm', width: 25, height: 6, px: { w: 354, h: 85 } },
      { id: '6x40', name: '6 × 40mm', width: 40, height: 6, px: { w: 567, h: 85 } },
      { id: '6x60', name: '6 × 60mm', width: 60, height: 6, px: { w: 850, h: 85 } },
    ]
  },
  '9mm': {
    tapeWidth: 9,
    printableHeight: Math.round(9 * MM_TO_PX),
    labels: [
      { id: '9x25', name: '9 × 25mm', width: 25, height: 9, px: { w: 354, h: 128 } },
      { id: '9x40', name: '9 × 40mm', width: 40, height: 9, px: { w: 567, h: 128 } },
      { id: '9x60', name: '9 × 60mm', width: 60, height: 9, px: { w: 850, h: 128 } },
      { id: '9x80', name: '9 × 80mm', width: 80, height: 9, px: { w: 1134, h: 128 } },
    ]
  },
  '12mm': {
    tapeWidth: 12,
    printableHeight: Math.round(12 * MM_TO_PX),
    labels: [
      { id: '12x25', name: '12 × 25mm', width: 25, height: 12, px: { w: 354, h: 170 } },
      { id: '12x40', name: '12 × 40mm', width: 40, height: 12, px: { w: 567, h: 170 } },
      { id: '12x60', name: '12 × 60mm', width: 60, height: 12, px: { w: 850, h: 170 } },
      { id: '12x80', name: '12 × 80mm', width: 80, height: 12, px: { w: 1134, h: 170 } },
      { id: '12x100', name: '12 × 100mm', width: 100, height: 12, px: { w: 1417, h: 170 } },
    ]
  },
  '18mm': {
    tapeWidth: 18,
    printableHeight: Math.round(18 * MM_TO_PX),
    labels: [
      { id: '18x25', name: '18 × 25mm', width: 25, height: 18, px: { w: 354, h: 255 } },
      { id: '18x40', name: '18 × 40mm', width: 40, height: 18, px: { w: 567, h: 255 } },
      { id: '18x60', name: '18 × 60mm', width: 60, height: 18, px: { w: 850, h: 255 } },
      { id: '18x80', name: '18 × 80mm', width: 80, height: 18, px: { w: 1134, h: 255 } },
      { id: '18x100', name: '18 × 100mm', width: 100, height: 18, px: { w: 1417, h: 255 } },
    ]
  },
  '24mm': {
    tapeWidth: 24,
    printableHeight: Math.round(24 * MM_TO_PX),
    labels: [
      { id: '24x25', name: '24 × 25mm', width: 25, height: 24, px: { w: 354, h: 340 } },
      { id: '24x40', name: '24 × 40mm', width: 40, height: 24, px: { w: 567, h: 340 } },
      { id: '24x60', name: '24 × 60mm', width: 60, height: 24, px: { w: 850, h: 340 } },
      { id: '24x80', name: '24 × 80mm', width: 80, height: 24, px: { w: 1134, h: 340 } },
      { id: '24x100', name: '24 × 100mm', width: 100, height: 24, px: { w: 1417, h: 340 } },
    ]
  }
};

// Flatten all labels into a single array
export const ALL_LABELS = Object.values(LABEL_CATALOG).flatMap(group => group.labels);

// Get label spec by ID
export const getLabelById = (id) => ALL_LABELS.find(l => l.id === id);

/**
 * Generate label with fixed layout (SYNCHRONOUS — no async):
 * LEFT side: Line 1 = Make Model, Year  |  Line 2 = Chassis(5), Color
 * RIGHT side: QR code
 */
function truncate(text, ctx, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  while (text.length > 0 && ctx.measureText(text + '…').width > maxWidth) {
    text = text.slice(0, -1);
  }
  return text + '…';
}

export function generateBrotherLabel(unit, labelSpec) {
  const { w, h } = labelSpec.px;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  // White background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#000000';

  const pad = Math.max(4, Math.round(6 * (h / 340)));

  // QR on right side
  const qrSize = h - pad * 2;
  const qrX = w - qrSize - pad;
  const qrY = pad;
  drawQRSync(ctx, unit, qrX, qrY, qrSize);

  // Text on left side
  const textW = qrX - pad * 2;
  const makeModel = `${unit.make || ''} ${unit.model || ''}`.trim();
  const year = unit.year ? String(unit.year) : '';
  const line1 = [makeModel, year].filter(Boolean).join(', ');

  const chassis5 = (unit.chassis || unit.chassisNumber || '').substring(0, 5);
  const color = unit.color || '';
  const line2 = [chassis5, color].filter(Boolean).join(', ');

  // Font sizes scaled to label height
  const line1Size = Math.max(10, Math.round(h * 0.22));
  const line2Size = Math.max(8, Math.round(h * 0.16));

  // Line 1: Make Model, Year (bold)
  ctx.font = `bold ${line1Size}px Arial`;
  ctx.textBaseline = 'middle';
  const y1 = Math.round(h * 0.38);
  ctx.fillText(truncate(line1, ctx, textW), pad, y1);

  // Line 2: Chassis(5), Color
  ctx.font = `${line2Size}px Arial`;
  const y2 = Math.round(h * 0.68);
  ctx.fillText(truncate(line2, ctx, textW), pad, y2);

  return canvas;
}

/**
 * Draw QR code synchronously using QRCode.create() + manual canvas drawing
 */
function drawQRSync(ctx, unit, x, y, size) {
  const qrData = `${window.location.origin}/?u=${unit.sku || unit.id}`;

  try {
    // QRCode.create returns the raw QR matrix synchronously
    const createFn = QRCode.create || (QRCode.default && QRCode.default.create);
    if (!createFn) throw new Error('no create');

    const qr = createFn(qrData, { errorCorrectionLevel: 'M' });
    const data = qr.modules.data;
    const moduleCount = qr.modules.size;
    const margin = 1;
    const totalModules = moduleCount + margin * 2;
    const cellSize = size / totalModules;

    // White background for QR area
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(x, y, size, size);

    // Draw black modules
    ctx.fillStyle = '#000000';
    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        if (data[row * moduleCount + col]) {
          ctx.fillRect(
            x + (col + margin) * cellSize,
            y + (row + margin) * cellSize,
            Math.ceil(cellSize),
            Math.ceil(cellSize)
          );
        }
      }
    }
  } catch (e) {
    // Fallback: draw a placeholder — the async QR can be layered on later
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(x, y, size, size);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 2, y + 2, size - 4, size - 4);
    // Draw a simple pattern so it's not just empty
    const s = Math.floor(size / 7);
    ctx.fillStyle = '#000';
    // Corner squares (like real QR finder patterns)
    [
      [x + 2, y + 2], [x + size - s - 2, y + 2], [x + 2, y + size - s - 2]
    ].forEach(([cx, cy]) => {
      ctx.fillRect(cx, cy, s, s);
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(cx + Math.floor(s * 0.2), cy + Math.floor(s * 0.2), Math.floor(s * 0.6), Math.floor(s * 0.6));
      ctx.fillStyle = '#000';
      ctx.fillRect(cx + Math.floor(s * 0.35), cy + Math.floor(s * 0.35), Math.floor(s * 0.3), Math.floor(s * 0.3));
    });
    ctx.font = `${Math.max(8, Math.round(size * 0.08))}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(unit.sku || unit.id || '', x + size / 2, y + size - 4);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }
}

/**
 * Print a label via browser print dialog
 * @param {HTMLCanvasElement} canvas
 * @param {Object} labelSpec - optional, { width, height } in mm
 */
export function printLabel(canvas, labelSpec) {
  const dataUrl = canvas.toDataURL('image/png');
  // Label dimensions in mm (width = tape length direction, height = tape width)
  const wMm = labelSpec?.width || 40;
  const hMm = labelSpec?.height || 24;
  const printWindow = window.open('', '_blank', 'width=600,height=300');
  if (!printWindow) {
    alert('Please allow popups to print labels');
    return;
  }
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Print Label</title>
      <style>
        @page {
          size: ${wMm}mm ${hMm}mm;
          margin: 0;
        }
        * { margin: 0; padding: 0; }
        body {
          width: ${wMm}mm;
          height: ${hMm}mm;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
        }
        img {
          width: ${wMm}mm;
          height: ${hMm}mm;
          object-fit: contain;
          image-rendering: pixelated;
        }
        @media screen {
          body {
            margin: 20px auto;
            border: 1px dashed #ccc;
          }
        }
      </style>
    </head>
    <body>
      <img src="${dataUrl}" />
      <script>
        window.onload = function() {
          setTimeout(function() { window.print(); }, 300);
          window.onafterprint = function() { window.close(); };
        };
      </script>
    </body>
    </html>
  `);
  printWindow.document.close();
}

/**
 * Download label as PNG
 */
export function downloadLabel(canvas, filename) {
  const link = document.createElement('a');
  link.download = filename || 'label.png';
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/**
 * Save print to log
 */
export function logPrint(unit, labelSpec) {
  const logs = JSON.parse(localStorage.getItem('bigstar_print_log') || '[]');
  logs.unshift({
    id: Date.now().toString(36),
    unitId: unit.id,
    unitName: unit.stockNumber || unit.partName || unit.id,
    labelSize: labelSpec.name,
    timestamp: new Date().toISOString(),
    user: JSON.parse(localStorage.getItem('bigstar_session') || '{}').name || 'Unknown'
  });
  if (logs.length > 500) logs.length = 500;
  localStorage.setItem('bigstar_print_log', JSON.stringify(logs));
}
